/**
 * Starting points for a new workflow. These are DRAFTS the editor opens
 * pre-filled — the author still saves it themselves, so nothing is seeded
 * server-side and the UI-only authoring rule holds.
 *
 * A template that names a platform trigger fills it in; the rest leave it
 * empty so the picker opens on an event the tenant chooses. Every one leaves
 * real holes (a destination, a threshold) — a template is a head start, not
 * a thing to turn on unread.
 */
import {
  Bot,
  MessageSquareWarning,
  ShieldCheck,
  TrendingUp,
  type LucideIcon,
} from '@servicecompressionllc/sc-design-system/icons';
import type { BuilderDefinition } from '../../lib/types.js';

/**
 * The filter chips over the gallery. Named after what a template STARTS FROM,
 * never after an industry's nouns: the platform's own events are a fixed set
 * we own, and everything else is whatever the tenant sends us. A family called
 * "Units" would have been our domain leaking into every tenant's admin UI.
 */
export const TEMPLATE_FAMILIES = ['Feedback', 'Your own events'] as const;
export type TemplateFamily = (typeof TEMPLATE_FAMILIES)[number];

export interface WorkflowTemplate {
  id: string;
  /** What it does, as the author would say it. */
  title: string;
  blurb: string;
  /** The step kinds it uses, for the card's chips. */
  kinds: string[];
  family: TemplateFamily;
  icon: LucideIcon;
  /** Suggested workflow name — the author can rename before saving. */
  name: string;
  /** Empty = the editor opens on the trigger picker. */
  trigger: string;
  definition: BuilderDefinition;
}

const notify = (title: string, body: string) => ({
  type: 'notify' as const,
  channelIds: [],
  title,
  body,
});

export const WORKFLOW_TEMPLATES: WorkflowTemplate[] = [
  {
    id: 'route-feedback',
    family: 'Feedback',
    icon: MessageSquareWarning,
    title: 'Route feedback to the right place',
    blurb:
      'When a feedback item is triaged, tell someone about the content problems and leave the rest alone.',
    kinds: ['Split', 'Message'],
    name: 'Route feedback',
    trigger: 'feedback_triaged',
    definition: {
      v: 1,
      createCase: false,
      steps: [
        {
          type: 'branch',
          cases: [
            {
              label: 'Knowledge content',
              when: {
                match: 'all',
                conditions: [{ path: 'payload.category', op: 'eq', value: 'knowledge_base' }],
              },
              steps: [
                notify(
                  'Feedback needs a look — {{payload.category}}',
                  '{{payload.summary}}\nHow serious: {{payload.severity}}',
                ),
              ],
            },
          ],
          otherwise: [],
        },
      ],
    },
  },
  {
    id: 'escalate-a-spike',
    family: 'Your own events',
    icon: TrendingUp,
    title: 'Escalate an unusual spike',
    blurb:
      'When a number comes in above what you would expect, ask someone to sign off before anything else happens.',
    kinds: ['Only if', 'Ask a person', 'Message'],
    name: 'Escalate a spike',
    trigger: '',
    definition: {
      v: 1,
      createCase: false,
      steps: [
        { type: 'filter', match: 'all', conditions: [{ path: '', op: 'gt' }] },
        {
          type: 'approval',
          approvers: [],
          title: 'This looks unusual — should we act on it?',
          onApprove: [notify('Approved', 'Going ahead with it.')],
          onDecline: [],
        },
      ],
    },
  },
  {
    id: 'agent-investigates',
    family: 'Your own events',
    icon: Bot,
    title: 'Have an AI agent look into it',
    blurb: 'Let an agent look things up itself, then send what it found to a person.',
    kinds: ['AI investigates', 'Message'],
    name: 'Investigate and report',
    trigger: '',
    definition: {
      v: 1,
      createCase: false,
      steps: [
        {
          type: 'agent',
          name: 'Investigate',
          personaId: '',
          task: 'Work out what is going on and how urgent it is.',
        },
        notify('{{steps.Investigate.result.summary}}', '{{steps.Investigate.result.detail}}'),
      ],
    },
  },
  {
    id: 'human-first',
    family: 'Your own events',
    icon: ShieldCheck,
    title: 'Check with a human first',
    blurb:
      'Nothing leaves the platform until someone approves it — and the workflow carries on either way they answer.',
    kinds: ['Ask a person', 'Message'],
    name: 'Check with a human first',
    trigger: '',
    definition: {
      v: 1,
      createCase: false,
      steps: [
        {
          type: 'approval',
          approvers: [],
          title: 'Send this on?',
          onApprove: [notify('Sending it on', 'Approved — going ahead.')],
          onDecline: [notify('Held back', 'Declined — nothing was sent.')],
        },
      ],
    },
  },
] as WorkflowTemplate[];

export const templateById = (id: string | null): WorkflowTemplate | undefined =>
  id ? WORKFLOW_TEMPLATES.find((t) => t.id === id) : undefined;
